import { createSelector } from "@reduxjs/toolkit"
import { selectProductPlan } from "./limsProductTestPlan"
import { selectRerunForOriginal } from "./limsReruns"
import { WORKLIST_TEMPLATE_STATES } from "./limsWorklistTemplates"
import { SAMPLE_WORKLIST_STATES } from "./limsSampleWorklists"
import { selectSampleReleaseStatus, RELEASE_STATUS } from "./limsRelease"

// Sample/lot-level QA completeness (F6, SPEC-038). QA disposition moved from analysis level to
// sample/lot level: a samplenr/lotnr becomes QA-ready only once it is COMPLETE, evaluated **per
// samplenr/lotnr ACROSS ALL the analysis runs** that touched it (the analysis × sample matrix —
// one analysis run covers many samples; one sample needs many analyses spread over several runs).
//
// "Complete" (mirrors the server-authoritative release gate in limsRelease.js / release.py, which
// stays the authority — these selectors only DRIVE THE WORKLIST + the fail-fast UX):
//   - every REQUIRED analysis for the samplenr/lotnr (the Product Test Plan, F-B2) has a result
//     that has CLEARED REVIEW (supervisor, else reviewer, signed it on to QA — state "sent-to-qa"
//     or already QA-decided), AND
//   - no OPEN OOS/OOT investigation is on any of the group's sample-analyses, AND
//   - every rerun is FINISHED (a qa-rerun result is superseded by a created rerun; no rerun is
//     still pending) — reusing the may-reanalyse disposition; there is no separate rerun approval.
//
// The "samplenr/lotnr" unit is the Sample-View group key (productName, sampleName, observationId);
// its sample-analyses are EVERY (analysis, sample) pair under that triple (potentially many runs,
// each a distinct sampleId). This is the SAME group key the release uses.

// Result lifecycle states (kept local so this slice doesn't import the whole limsResults surface;
// the string values are the single source of truth in limsResults.js).
const SENT_TO_QA = "sent-to-qa"
const QA_APPROVED = "qa-approved"
const QA_RERUN = "qa-rerun"
const QA_STOPPED = "qa-stopped"

// Reasons a samplenr/lotnr is NOT yet QA-complete (i18n keys; the worklist/Sample View surface them).
export const COMPLETENESS_REASON = {
  MISSING_RESULT: "limsSampleQaReasonMissingResult", // a required analysis has no cleared result yet
  IN_REVIEW: "limsSampleQaReasonInReview", // a required analysis is still in the review pipeline
  OPEN_INVESTIGATION: "limsSampleQaReasonOpenInvestigation", // an OOS/OOT investigation is open
  RERUN_PENDING: "limsSampleQaReasonRerunPending", // a rerun was ordered but is not finished
  STOPPED: "limsSampleQaReasonStopped", // a result was QA-stopped (an investigation rejected the batch)
  WORKLIST_UNSIGNED: "limsSampleQaReasonWorklistUnsigned", // F9: a required sample-prep worklist is not signed
}

// Whether a result has cleared review and is in (or past) the QA stage for completeness purposes.
function hasClearedReview(result) {
  const s = result && result.state
  return s === SENT_TO_QA || s === QA_APPROVED || s === QA_RERUN || s === QA_STOPPED
}

// The Sample-View group matching one (productName, sampleName, observationId) triple, or null.
// Note: the Sample View stores one top-level entry per sampleName carrying ALL its analyses; the
// screen splits them by observationId for display, so we filter the analyses to the triple here.
function selectGroupForSample(state, { productName, sampleName, observationId }) {
  const groups = (state.sampleView && state.sampleView.sampleGroups) || []
  const normP = (p) => (p === "" || p == null ? null : p)
  const wantP = normP(productName)
  const wantObs = observationId == null ? null : String(observationId)
  const match = groups.find(
    (g) => g.sampleName === sampleName && normP(g.productName) === wantP
  )
  if (!match) return null
  const analyses = (match.analyses || []).filter(
    (a) => (wantObs == null ? true : String(a.observationId ?? match.observationId) === wantObs)
  )
  return { ...match, productName: match.productName, observationId, analyses }
}

// The set of analysis-definition ids + names the Product Test Plan REQUIRES for a product. Falls
// back to the analyses actually run on the sample (the group's analysis names) when the product has
// no curated plan, so a product without a plan still has a sensible required set. Returns
// { ids:Set, names:Set, hasCuratedPlan:boolean }. Reads the RAW curated ids (selectProductPlan) +
// resolves their names from the definitions map directly — deliberately NOT via selectProductTestPlan,
// which also resolves each spec (state.limsSpecs) the completeness check does not need.
function requiredAnalysesForProduct(state, productName, group) {
  const plan = selectProductPlan(state, productName)
  const curatedIds = (plan && plan.analysisDefinitionIds) || []
  if (curatedIds.length > 0) {
    const defsById = state.analysisDefinitions || {}
    const names = curatedIds.map((id) => (defsById[id] && defsById[id].name) || null).filter(Boolean)
    return { ids: new Set(curatedIds), names: new Set(names), hasCuratedPlan: true }
  }
  // No curated plan: require the analyses actually present on the sample group (distinct by name).
  const names = new Set((group.analyses || []).map((a) => a.name).filter(Boolean))
  return { ids: new Set(), names, hasCuratedPlan: false }
}

// Does a group sample-analysis satisfy a required analysis? Match by analysis-definition id when
// both carry one, else by analysis name (the conservative join used across the LIMS slices — the
// deployed analysis has no stable definition id, so name is the reliable key).
function analysisMatchesRequired(groupAnalysis, requiredIds, requiredName) {
  if (groupAnalysis.analysisDefinitionId && requiredIds.has(groupAnalysis.analysisDefinitionId)) return true
  return !!requiredName && groupAnalysis.name === requiredName
}

// All QC results for a set of sample ids (the group's sample-analyses), by sampleId.
function resultsForSampleIds(state, sampleIds) {
  const set = new Set(sampleIds.filter(Boolean))
  return ((state.limsResults && state.limsResults.items) || []).filter((r) => set.has(r.sampleId))
}

// Whether ANY non-closed OOS/OOT is on one of the group's sample-analyses. FE SPEC-048 (F4):
// counts every not-yet-closed OOS — INCLUDING a phase1-null "raise" — mirroring the backend
// release gate (has_open_investigation, BE SPEC-048 R5). This completeness reason feeds the QA
// decision worklist (selectQaSampleWorklist): a group with any open OOS must not be offered as
// "awaiting decision", because the decision's release would be refused by the server anyway.
// The OOS itself is decided via the Investigations / QA-review screens as usual.
function hasOpenInvestigation(state, sampleIds) {
  const set = new Set(sampleIds.filter(Boolean))
  return ((state.limsOos && state.limsOos.items) || []).some((o) => set.has(o.sampleId) && o.state !== "closed")
}

// Whether ONE sample's own results PASS the QC gate — a faithful mirror of the server's
// `_sample_gate_passes` (release.py): at least one result; every result `qa-approved`, or a
// `qa-rerun` itself superseded by a passing rerun (recursive along the rerun chain); any
// non-terminal (draft / in-review / sent-to-qa) or `qa-stopped` blocks; a required-but-unsigned
// sample-prep worklist blocks (F9). `visited` breaks a cyclic rerun chain (a rerun can never vouch
// for itself). This is how we decide a rerun has actually FINISHED, not merely been created.
function sampleGatePasses(state, sampleId, visited) {
  if (!sampleId || visited.has(sampleId)) return false
  visited.add(sampleId)
  const results = ((state.limsResults && state.limsResults.items) || []).filter((r) => r.sampleId === sampleId)
  if (results.length === 0) return false
  if (!worklistsComplete(state, results)) return false
  return results.every((r) => {
    if (r.state === QA_APPROVED) return true
    if (r.state === QA_RERUN) {
      const link = selectRerunForOriginal(state, r.sampleId)
      return !!(link && link.rerunSampleId) && sampleGatePasses(state, link.rerunSampleId, visited)
    }
    return false // draft / in-review / sent-to-qa (undecided) and qa-stopped all block
  })
}

// Whether every rerun ordered for the group is FINISHED. A `qa-rerun` result is finished only when
// its rerun sample's OWN results PASS the gate (sampleGatePasses) — qa-approved, or recursively
// superseded. SPEC-038 / SPEC-010: no separate rerun approval; the rerun simply has to clear QC the
// same way. Mirrors release.py `_superseded_by_passing_rerun` so a created-but-still-pending /
// failing rerun is NOT mistaken for finished (which made a lot show as QA-ready while the
// authoritative release gate rejected it). Returns true when there are no pending reruns.
function rerunsFinished(state, results) {
  const reruns = results.filter((r) => r.state === QA_RERUN)
  if (reruns.length === 0) return true
  return reruns.every((r) => {
    const link = selectRerunForOriginal(state, r.sampleId)
    return !!(link && link.rerunSampleId) && sampleGatePasses(state, link.rerunSampleId, new Set([r.sampleId]))
  })
}

// F9 (SPEC-041): every sample-analysis whose analysis definition has an EFFECTIVE worklist template
// must have a SIGNED per-sample worklist for sample-prep completeness (mirrors the server gate in
// release.py). True when no such requirement is unmet. We key off the group's results (each carries
// its analysisDefinitionId) and the loaded worklist-template + sample-worklist slices. Definitions
// without an effective template are unaffected (back-compatible).
function worklistsComplete(state, results) {
  const templates = (state.limsWorklistTemplates && state.limsWorklistTemplates.items) || []
  const hasEffectiveTemplate = (defId) =>
    !!defId && templates.some((t) => t.analysisDefinitionId === defId && t.state === WORKLIST_TEMPLATE_STATES.EFFECTIVE)
  const worklists = (state.limsSampleWorklists && state.limsSampleWorklists.items) || []
  const isSigned = (sampleId, analysisId) =>
    worklists.some(
      (w) => w.sampleId === sampleId && w.analysisId === analysisId && w.state === SAMPLE_WORKLIST_STATES.SIGNED
    )
  return results.every((r) => !hasEffectiveTemplate(r.analysisDefinitionId) || isSigned(r.sampleId, r.analysisId))
}

// PUBLIC: completeness of one samplenr/lotnr across all its analysis runs. Returns
// { complete:boolean, reasons:[i18nKey], group, requiredCount, signedCount }. A non-existent group
// is never complete (no analyses to certify).
export const selectSampleQaCompleteness = (state, args) => {
  const group = selectGroupForSample(state, args || {})
  if (!group || (group.analyses || []).length === 0) {
    return { complete: false, reasons: [COMPLETENESS_REASON.MISSING_RESULT], group: null, requiredCount: 0, signedCount: 0 }
  }
  const productName = group.productName
  const sampleIds = (group.analyses || []).map((a) => a.sampleId).filter(Boolean)
  const results = resultsForSampleIds(state, sampleIds)
  const { ids: requiredIds, names: requiredNames } = requiredAnalysesForProduct(state, productName, group)

  const reasons = new Set()

  // A QA-stopped result (batch rejected) is a standing block — surfaced distinctly.
  if (results.some((r) => r.state === QA_STOPPED)) reasons.add(COMPLETENESS_REASON.STOPPED)

  // Every REQUIRED analysis must have a result that has cleared review on one of the group's
  // sample-analyses. "Required" = the union of curated ids + names (or the present names when no
  // curated plan). We check each required name (the stable join key) against the group.
  requiredNames.forEach((reqName) => {
    const groupMatches = (group.analyses || []).filter((a) =>
      analysisMatchesRequired(a, requiredIds, reqName)
    )
    if (groupMatches.length === 0) {
      // The product's plan requires this analysis but it is not even on the sample yet.
      reasons.add(COMPLETENESS_REASON.MISSING_RESULT)
      return
    }
    const matchSampleIds = new Set(groupMatches.map((a) => a.sampleId).filter(Boolean))
    const matchResults = results.filter((r) => matchSampleIds.has(r.sampleId))
    if (matchResults.length === 0) {
      reasons.add(COMPLETENESS_REASON.MISSING_RESULT)
      return
    }
    // Some result exists for this required analysis; it must have cleared review (or be a finished
    // rerun chain). If none has cleared review, the analysis is still in the review pipeline.
    if (!matchResults.some((r) => hasClearedReview(r))) {
      reasons.add(COMPLETENESS_REASON.IN_REVIEW)
    }
  })

  if (hasOpenInvestigation(state, sampleIds)) reasons.add(COMPLETENESS_REASON.OPEN_INVESTIGATION)
  if (!rerunsFinished(state, results)) reasons.add(COMPLETENESS_REASON.RERUN_PENDING)
  // F9 (SPEC-041): a required-but-unsigned sample-prep worklist blocks completeness.
  if (!worklistsComplete(state, results)) reasons.add(COMPLETENESS_REASON.WORKLIST_UNSIGNED)

  const signedCount = results.filter((r) => hasClearedReview(r)).length
  return {
    complete: reasons.size === 0,
    reasons: Array.from(reasons),
    group,
    requiredCount: requiredNames.size,
    signedCount,
  }
}

// PUBLIC worklist: every samplenr/lotnr READY TO RELEASE — one row per Sample-View group
// (productName, sampleName, observationId) that is QA-complete, releasable and not yet released.
// The QA SampleReview screen reads this. Sorted by product then sample then observation.
//
// #4110/#4105: the screen now RELEASES ONLY — it no longer approves the group's pending results on
// the way past (per-analysis QA approval is its own signed step, ResultEntryPanel/QaDecisionSection).
// So a row must satisfy the RELEASE gate, not merely "cleared review": completeness alone counts a
// `sent-to-qa` result as cleared, which listed lots whose analyses nobody had QA-approved yet. They
// looked releasable, and release() then aborted at signing time with a reason nothing on this screen
// explained. Both checks are kept — they cover different halves of the server gate (release.py):
// completeness owns reruns + sample-prep worklists, the release status owns all-qa-approved +
// batch-rejected + already-released — so together they mirror it.
export const selectQaSampleWorklist = createSelector(
  [
    (state) => state.sampleView && state.sampleView.sampleGroups,
    (state) => state.limsResults && state.limsResults.items,
    (state) => state.limsOos && state.limsOos.items,
    (state) => state.limsReruns && state.limsReruns.items,
    (state) => state.limsProductTestPlan,
    (state) => state.limsRelease && state.limsRelease.items,
    (state) => state,
  ],
  (sampleGroups, _results, _oos, _reruns, _plan, releaseItems, state) => {
    const groups = sampleGroups || []
    const releasedKeys = new Set((releaseItems || []).map((r) => r.id))
    const rows = []
    // Expand each Sample-View group into its (sampleName, observationId) triples (the screen splits
    // by observationId; the worklist is per triple = the release group).
    groups.forEach((g) => {
      const byObs = new Set()
      ;(g.analyses || []).forEach((a) => byObs.add(String(a.observationId ?? g.observationId ?? "")))
      byObs.forEach((obs) => {
        const observationId = obs === "" ? (g.observationId ?? "") : obs
        const key = `${g.productName || ""}|${g.sampleName || ""}|${observationId ?? ""}`
        if (releasedKeys.has(key)) return // already released — leaves the worklist
        const completeness = selectSampleQaCompleteness(state, {
          productName: g.productName,
          sampleName: g.sampleName,
          observationId,
        })
        if (!completeness.complete) return
        // #4110: and it must actually be releasable — every analysis QA-approved, no terminal
        // block. Anything short of READY belongs on the analysis screens, not here.
        if (selectSampleReleaseStatus(state, completeness.group) !== RELEASE_STATUS.READY) return
        rows.push({
          key,
          productName: g.productName || null,
          sampleName: g.sampleName || null,
          observationId,
          group: completeness.group,
          signedCount: completeness.signedCount,
          requiredCount: completeness.requiredCount,
        })
      })
    })
    return rows.sort((a, b) => {
      const p = (a.productName || "").localeCompare(b.productName || "")
      if (p !== 0) return p
      const s = (a.sampleName || "").localeCompare(b.sampleName || "")
      if (s !== 0) return s
      return String(a.observationId || "").localeCompare(String(b.observationId || ""))
    })
  }
)
